import { MutableRefObject } from 'react'
import {
  GridColDef,
  GridColumnGroupingModel,
  GridPinnedColumnFields,
  GridRenderCellParams,
} from '@microservices/wiskey-react-table-component'
import { Interpolation, Theme } from '@mui/material'

import { ObjectXCell } from '@components/TimeLineGrid/components/ObjectXCell'

import { COLUMN_TYPE, TimelineCellContext, TimeLineDTOType, TimeLineGridColumn } from '@types'

import { ObjectYCell } from '../components/ObjectYCell'
import { useHandlers } from '../hooks'

import { groupTitleCellRenderer } from './group/groupTitleCellRenderer'
import { getColumnProperties } from './getColumnProperties'
import { getDateIntervalCellStyles } from './getDateIntervalCellStyles'
import { getPinnedColumns } from './getPinnedColumns'
import { isBottomPinnedTotalRow } from './isBottomPinnedTotalRow'
import { isGroupTitleRow } from './isGroupTitleRow'
import { PINNED_COLUMN } from '@constants'

type Params = {
  config: TimeLineDTOType
  dynamicColumns: GridColDef[]
  handlers: ReturnType<typeof useHandlers>
  cellContextRef: MutableRefObject<TimelineCellContext>
}

export const getColumns = (
  params: Params
): {
  columns: GridColDef[]
  styles: Interpolation<Theme>
  pinnedColumns: GridPinnedColumnFields
  columnGroupingModel: GridColumnGroupingModel
} => {
  const { config, dynamicColumns, handlers, cellContextRef } = params

  let styles = {}

  const yColumns = config.objectY.columns || []
  const xColumns = config.objectX?.columns || []

  const columns: TimeLineGridColumn[] = yColumns.map(item => {
    const { style, column } = getColumnProperties(item, {
      columnType: COLUMN_TYPE.OBJECT_Y,
      colSpan: (_, row) => {
        if (isGroupTitleRow(row.id) && !item.pinnedColumn) {
          return (
            yColumns.filter(item => !item.pinnedColumn).length +
            dynamicColumns.length +
            xColumns.filter(item => !item.pinnedColumn).length
          )
        }

        return 1
      },
      sortable: true,
      resizable: true,
      renderCell: (params: GridRenderCellParams) => {
        if (isGroupTitleRow(params.id as string)) {
          if (item.pinnedColumn) {
            return ''
          }

          return groupTitleCellRenderer({ params, config })
        }

        if (isBottomPinnedTotalRow(params.row.id)) {
          const yColumnsWithoutPinned = yColumns.filter(item => !item.pinnedColumn)
          const leftPinnedColumns = yColumns.filter(
            item => item.pinnedColumn === PINNED_COLUMN.LEFT
          )

          if (
            (!item.pinnedColumn &&
              yColumnsWithoutPinned[yColumnsWithoutPinned.length - 1]?.id === item.id) ||
            (!yColumnsWithoutPinned.length &&
              leftPinnedColumns?.length &&
              leftPinnedColumns[leftPinnedColumns.length - 1]?.id === item.id)
          ) {
            return params.row.title
          }

          return ''
        }

        return <ObjectYCell config={config} handlers={handlers} item={item} params={params} />
      },
    })

    styles = {
      ...styles,
      ...style,
    }

    return column as TimeLineGridColumn
  })

  columns.push(...dynamicColumns)

  xColumns.forEach(item => {
    const { style, column } = getColumnProperties(item, {
      columnType: COLUMN_TYPE.OBJECT_X,
      resizable: true,
      colSpan: (_, row) => {
        if (isGroupTitleRow(row.id) && !item.pinnedColumn) {
          return (
            yColumns.filter(item => !item.pinnedColumn).length +
            dynamicColumns.length +
            xColumns.filter(item => !item.pinnedColumn).length
          )
        }

        return 1
      },
      renderCell: (params: GridRenderCellParams) => {
        return (
          <ObjectXCell
            cellContextRef={cellContextRef}
            handlers={handlers}
            item={item}
            params={params}
          />
        )
      },
    })

    styles = {
      ...styles,
      ...style,
    }

    columns.push(column)
  })

  styles = {
    ...styles,
    ...getDateIntervalCellStyles(),
  }

  return {
    columns,
    styles,
    pinnedColumns: getPinnedColumns(yColumns.concat(xColumns)),
    columnGroupingModel: [],
    // columnGroupingModel: getColumnGroupingModelByMonth(dateIntervals, yColumns, xColumns), // TODO доработать группировку по месяцу
  }
}
